import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';

export const METRIC_TOOLTIPS = {
  spend: 'Total cost of your ad campaign',
  spends: 'Total cost of your ad campaign',
  ad_spend: 'Total cost of your ad campaign',
  roi: 'Return on Investment',
  roas: 'Return on Investment',
  views: 'Total times your ad was shown',
  impressions: 'Total times your ad was shown',
  clicks: 'Total clicks received on your ad',
  ctr: 'Percentage of views converted into clicks',
  total_converted_units: 'Total units sold through the campaign',
  units_sold: 'Total units sold through the campaign',
  cvr: 'Percentage of clicks converted into purchases',
  revenue: 'Total sales generated from the campaign',
  total_revenue: 'Total sales generated from the campaign',
  sales: 'Total sales generated from the campaign',
  acos: 'Percentage of sales spent on ads',
  tacos: 'Percentage of total sales spent on ads',
  campaign_name: 'Unique name of the campaign',
  pla: 'Product Listing Ads',
  pca: 'Product Contextual Ads',
  'brand ads': 'Brand Advertising Campaigns',
  'sponsored products': 'Amazon advertising format for individual product listings',
  'sponsored brand': 'Amazon advertising format for branded creatives and product collections',
  'sponsored display': 'Amazon display advertising format for audience-based placements',
  'ono dsp': 'Amazon Owned & Operated Display Side Platform — Insertion Order + Line Item level DSP campaigns running across Amazon O&O properties.',
  '3p dsp': 'Third-Party Display Side Platform — audience-based display buying across third-party inventory outside Amazon O&O.',
  'telegram ads': 'Paid advertising placements on the Telegram messenger platform.',
  spends_alloc: 'Percentage of total spends allocated to this property',
  sales_alloc: 'Percentage of total revenue allocated to this property',
};

export const getTooltip = (key) => {
  if (!key) return '';
  const lower = key.toLowerCase();
  if (METRIC_TOOLTIPS[lower]) return METRIC_TOOLTIPS[lower];
  const normalized = lower.replace(/[^a-z0-9]/g, '_');
  return METRIC_TOOLTIPS[normalized] || '';
};

const TOOLTIP_STYLE = {
  background: '#1e1e2f',
  color: '#ffffff',
  fontSize: '13px',
  lineHeight: '1.55',
  padding: '10px 14px',
  borderRadius: '8px',
  minWidth: '220px',
  maxWidth: '280px',
  boxShadow: '0 8px 30px rgba(0,0,0,0.3)',
  whiteSpace: 'normal',
  wordWrap: 'break-word',
  textAlign: 'left',
  fontWeight: 400,
  position: 'fixed',
  zIndex: 999999,
  pointerEvents: 'none',
};

const TooltipPortal = ({ rect, text }) => {
  const tipRef = useRef(null);
  const [pos, setPos] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (!rect || !tipRef.current) return;
    const tip = tipRef.current.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const gap = 8;

    let top, left;

    // Vertical: prefer above, fallback below
    if (rect.top - tip.height - gap > 0) {
      top = rect.top - tip.height - gap;
    } else {
      top = rect.bottom + gap;
    }

    // Horizontal: center on icon, clamp to viewport
    left = rect.left + rect.width / 2 - tip.width / 2;
    if (left < 8) left = 8;
    if (left + tip.width > vw - 8) left = vw - tip.width - 8;

    setPos({ top, left });
  }, [rect]);

  return createPortal(
    <div ref={tipRef} style={{ ...TOOLTIP_STYLE, top: pos.top, left: pos.left }}>
      {text}
    </div>,
    document.body
  );
};

export const MetricTooltip = ({ label, tip, alwaysShow = false }) => {
  const text = tip || getTooltip(label) || '';
  const [open, setOpen] = useState(false);
  const [rect, setRect] = useState(null);
  const btnRef = useRef(null);
  const closeTimer = useRef(null);

  const show = useCallback(() => {
    clearTimeout(closeTimer.current);
    if (btnRef.current) setRect(btnRef.current.getBoundingClientRect());
    setOpen(true);
  }, []);

  const hide = useCallback(() => {
    closeTimer.current = setTimeout(() => setOpen(false), 100);
  }, []);

  useEffect(() => () => clearTimeout(closeTimer.current), []);

  // Skip render only when we have neither tooltip text nor an explicit
  // `alwaysShow` opt-in. `alwaysShow` is used for surfaces where every row
  // must display an info mark regardless of description availability
  // (e.g. the Ad Property row names on Ad Format Breakdown).
  if (!text && !alwaysShow) return null;

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); if (text) show(); }}
        onMouseEnter={() => { if (text) show(); }}
        onMouseLeave={hide}
        onFocus={() => { if (text) show(); }}
        onBlur={hide}
        className="inline-flex items-center justify-center w-[15px] h-[15px] rounded-full border border-gray-300 text-[9px] font-bold text-gray-400 hover:border-[#32B4AC] hover:text-[#32B4AC] transition-colors focus:outline-none align-middle"
        style={{ cursor: text ? 'pointer' : 'default', userSelect: 'none', lineHeight: 0, flexShrink: 0 }}
        aria-label={text ? `${label} info` : `${label}`}
        tabIndex={text ? 0 : -1}
      >
        i
      </button>
      {open && rect && text && <TooltipPortal rect={rect} text={text} />}
    </>
  );
};
